import { useEffect, useState, useCallback, useMemo, type DragEvent } from "react";
import { appendUnique, firstPage, listedRows, nextPage, pickerPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Card, Button, Cluster, Drawer, Input, Stack, EmptyState, BoardColumn, KanbanCard, KanbanStack, StageFunnel, GroupTree, useToast } from "@salesapp/ui";
import { crmApi, type BoardDisplaySettings, type Lead, type LossReasonOption, type Opportunity, type OpportunityBoard } from "../../lib/crmApi";
import { opsApi } from "../../lib/opsApi";
import { parseAmount } from "../../lib/amount";
import { FIELD_HINTS, OPPORTUNITY_STAGE_OPTIONS } from "../../help/fieldHints";
import { useAccess } from "../../shell/AccessProvider";
import { CompanyLookup } from "./CompanyLookup";
import { FieldLabel } from "./FieldLabel";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { GroupByControl } from "./GroupByControl";
import { SearchBar, StatusSlicer } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { useCrmHref } from "../../lib/customerUrl";
import { ProductPicker, type PickedProductLine } from "./ProductPicker";
import { useLocale } from "../../shell/LocaleProvider";
import {
  bucketProjectedPipeline,
  projectedChartPoints,
  type ProjectedBucket,
  type ProjectedGrain,
} from "./projectedPipeline";
import { ProjectedLandingChart } from "./ProjectedLandingChart";
import { ResponsiveDataTable } from "./ResponsiveDataTable";
import { opportunityPersonName, personCompanyTitle } from "./recordLabels";
import { BoardDisplaySettingsPanel } from "./BoardDisplaySettings";
import { TabFieldOptionsSettings } from "./TabFieldOptionsSettings";
import { agingBackground, terminalNegativeBackground, terminalPositiveBackground } from "./boardDisplayColors";
import { groupCardsBy } from "./LeadsPage";
import { CloseLostModal } from "./CloseLostModal";
import { LossReasonSettingsPanel } from "./LossReasonSettings";
import { AskLoxleyButton } from "../../loxley/AskLoxleyButton";

const DEFAULT_STAGES: Array<{ value: string; label: string; hint: string }> = [
  { value: "lead_in", label: "Lead In", hint: OPPORTUNITY_STAGE_OPTIONS[0].meaning },
  { value: "qualifying", label: "Qualifying", hint: OPPORTUNITY_STAGE_OPTIONS[1].meaning },
  { value: "proposal", label: "Proposal", hint: OPPORTUNITY_STAGE_OPTIONS[2].meaning },
  { value: "negotiation", label: "Negotiation", hint: OPPORTUNITY_STAGE_OPTIONS[3].meaning },
  { value: "closed_won", label: "Closed Won", hint: OPPORTUNITY_STAGE_OPTIONS[4].meaning },
  { value: "closed_lost", label: "Closed Lost", hint: OPPORTUNITY_STAGE_OPTIONS[5].meaning },
];

type OppView = "board" | "table" | "projected";

type GroupBy = "none" | "salesperson" | "company" | "contact";

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "none", label: "No grouping" },
  { value: "salesperson", label: "Salesperson" },
  { value: "company", label: "Company" },
  { value: "contact", label: "Contact" },
];

function opportunityGroupKeyFor(opp: Opportunity, groupBy: GroupBy): { key: string; label: string } {
  if (groupBy === "salesperson") return { key: opp.owner_user_id ?? "—", label: opp.owner_name ?? "Unassigned" };
  if (groupBy === "contact") return { key: opp.id, label: opportunityPersonName(opp) || "No contact" };
  return { key: opp.company_id ?? "—", label: opp.company_name ?? "Unknown company" };
}

const EMPTY_PROJECTED: ProjectedBucket<Opportunity>[] = [];

function opportunityCardTitle(opp: Opportunity): string {
  return personCompanyTitle(
    opportunityPersonName(opp),
    opp.company_name,
    opp.title || "Untitled opportunity",
  );
}

function stageLabel(value: string | null | undefined): string {
  return DEFAULT_STAGES.find((stage) => stage.value === value)?.label ?? value ?? "Lead In";
}

function isWonOpportunity(opp: Opportunity): boolean {
  return Boolean(opp.won_at) || opp.stage === "closed_won";
}

function isLostOpportunity(opp: Opportunity): boolean {
  return Boolean(opp.lost_at) || opp.stage === "closed_lost";
}

function isTerminalOpportunity(opp: Opportunity): boolean {
  return isWonOpportunity(opp) || isLostOpportunity(opp);
}

function opportunityCardBackground(
  opp: Opportunity,
  settings: BoardDisplaySettings | null | undefined,
): string | undefined {
  if (isWonOpportunity(opp)) return terminalPositiveBackground(settings?.terminal_positive_color);
  if (isLostOpportunity(opp)) return terminalNegativeBackground(settings?.terminal_negative_color);
  return agingBackground(settings, opp.age_days);
}

function opportunityHref(opp: Opportunity, crmHref: (path: string) => string): string {
  if (isWonOpportunity(opp) && opp.order_id) {
    return crmHref(`/orders/${opp.order_id}`);
  }
  return crmHref(`/opportunities/${opp.id}`);
}

function wonOpportunityStatusLabel(opp: Opportunity): string {
  if (opp.order_number) return `Won: Order ${opp.order_number}`;
  return "Closed Won";
}

function ageLabel(days: number | null | undefined): string | null {
  if (days == null) return null;
  if (days <= 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function readView(raw: string | null): OppView {
  if (raw === "table" || raw === "projected") return raw;
  return "board";
}

function readGrain(raw: string | null): ProjectedGrain {
  return raw === "quarter" ? "quarter" : "month";
}

export function OpportunitiesPage({ orgId }: { orgId: string }) {
  const { can } = useAccess();
  const { show } = useToast();
  const { formatMoney, fiscalYearStartMonth } = useLocale();
  const money = (value: number | null | undefined) => (value == null ? undefined : formatMoney(value, undefined, true));
  const columnTotal = (cards: Opportunity[]) => {
    const sum = cards.reduce((acc, card) => acc + (card.value ?? 0), 0);
    return sum ? money(sum) : undefined;
  };
  const crmHref = useCrmHref();
  const navigate = useNavigate();
  const canMove = can("crm.opportunities", "update");
  const canConfigure = can("crm.opportunities", "configure");
  const canAge = can("crm.opportunities.aging");
  const canQuote = can("crm.quotes", "create") || can("crm.quotes", "manage") || can("crm.quotes", "update");
  const canForecast = can("crm.forecast");
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [board, setBoard] = useState<OpportunityBoard | null>(null);
  const [boardDisplay, setBoardDisplay] = useState<BoardDisplaySettings | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [periodTargets, setPeriodTargets] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [overStage, setOverStage] = useState<string | null>(null);
  const [quotingId, setQuotingId] = useState<string | null>(null);
  const [lossReasons, setLossReasons] = useState<LossReasonOption[]>([]);
  const [pendingCloseLost, setPendingCloseLost] = useState<Opportunity | null>(null);
  const [closeLostCodes, setCloseLostCodes] = useState<string[]>([]);
  const [closeLostComment, setCloseLostComment] = useState("");
  const [closingLost, setClosingLost] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    title: "",
    company_name: "",
    company_id: "",
    source_lead_id: "",
    value: "",
    stage: "lead_in",
    product_lines: [] as PickedProductLine[],
  });
  const openLeads = useMemo(() => leads.filter((lead) => !lead.converted_to_opportunity_id), [leads]);
  const view = readView(searchParams.get("view"));
  const openOnly = searchParams.get("open") === "1";
  const clearOpen = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("open");
    setSearchParams(params, { replace: true });
  };
  const grain = readGrain(searchParams.get("grain"));
  const stages = board?.stages?.length ? board.stages : DEFAULT_STAGES;
  const groupedOpportunities = useMemo(() => {
    if (groupBy === "none") return [];
    return groupCardsBy(opportunities, groupBy, opportunityGroupKeyFor);
  }, [opportunities, groupBy]);
  const projectedBuckets = useMemo(
    () =>
      view === "projected"
        ? bucketProjectedPipeline(opportunities, grain, new Date(), fiscalYearStartMonth)
        : EMPTY_PROJECTED,
    [view, opportunities, grain, fiscalYearStartMonth],
  );
  const chartPoints = useMemo(
    () =>
      projectedChartPoints(projectedBuckets, (key) => {
        if (periodTargets[key] != null) return periodTargets[key];
        const year = Number(key.slice(0, 4));
        if (!year || !periodTargets[`year:${year}`]) return 0;
        return grain === "month" ? periodTargets[`year:${year}`] / 12 : periodTargets[`year:${year}`] / 4;
      }),
    [projectedBuckets, periodTargets, grain],
  );

  const setView = (next: OppView) => {
    const params = new URLSearchParams(searchParams);
    if (next === "board") params.delete("view");
    else params.set("view", next);
    if (next !== "projected") params.delete("grain");
    else if (!params.get("grain")) params.set("grain", "month");
    setSearchParams(params, { replace: true });
  };

  const setGrain = (next: ProjectedGrain) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", "projected");
    if (next === "month") params.delete("grain");
    else params.set("grain", next);
    setSearchParams(params, { replace: true });
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(
        await crmApi.listOpportunities(orgId, stageFilter || undefined, search || undefined, nextPage(opportunities.length), {
          openOnly,
          withTotal: true,
        }),
      );
      const page = trimPage(listed.rows);
      setOpportunities((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more opportunities");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    setHasMore(false);
    try {
      const fetchOnce = () =>
        Promise.all([
          view === "board"
            ? openOnly
              ? crmApi.listOpportunityBoard(orgId, search || undefined, { openOnly: true })
              : crmApi.listOpportunityBoard(orgId, search || undefined)
            : view === "table"
              ? crmApi.listOpportunities(orgId, stageFilter || undefined, search || undefined, firstPage(), {
                  openOnly,
                  withTotal: true,
                })
              : crmApi.listOpportunities(orgId, stageFilter || undefined, search || undefined, pickerPage(), {
                  openOnly,
                }),
          crmApi.listLeads(orgId, undefined, undefined, undefined, pickerPage()).catch(() => [] as Lead[]),
        ]);
      // A cold start or an edge hiccup can drop the first response; retry once before reporting a failure.
      const [runData, leadData] = await fetchOnce().catch(
        () => new Promise<void>((resolve) => setTimeout(resolve, 800)).then(fetchOnce),
      );
      if (view === "board") {
        const data = runData as OpportunityBoard;
        setBoard(data);
        setBoardDisplay(data.board_display ?? null);
        const cards = Object.values(data.buckets).flat();
        setOpportunities(cards);
        setTotal(data.total ?? null);
      } else if (view === "table") {
        setBoard(null);
        const listed = listedRows(runData as unknown as Opportunity[]);
        const page = trimPage(listed.rows);
        setOpportunities(page.items);
        setHasMore(page.hasMore);
        setTotal(listed.total);
      } else {
        setBoard(null);
        const rows = runData as unknown as Opportunity[];
        setOpportunities(rows);
        setHasMore(false);
        setTotal(null);
      }
      setLeads(leadData);
    } catch (e) {
      setLoadFailed(true);
      setError(e instanceof Error ? e.message : "Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  }, [orgId, search, stageFilter, view, openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void crmApi.getLossReasons(orgId).then((res) => setLossReasons(res.reasons)).catch(() => setLossReasons([]));
  }, [orgId]);

  useEffect(() => {
    if (view !== "projected" || !canForecast) {
      setPeriodTargets({});
      return;
    }
    const years = [
      ...new Set(
        projectedBuckets
          .filter((bucket) => bucket.key !== "unscheduled")
          .map((bucket) => Number(bucket.key.slice(0, 4)))
          .filter(Boolean),
      ),
    ];
    if (years.length === 0) {
      const year = new Date().getUTCFullYear();
      years.push(year, year + 1);
    }
    let cancelled = false;
    void (async () => {
      const next: Record<string, number> = {};
      await Promise.all(
        years.map(async (year) => {
          try {
            const forecast = await opsApi.forecast(orgId, year, "detail");
            const yearly = Number(forecast.target_total) || 0;
            next[`year:${year}`] = yearly;
            for (const person of forecast.people ?? []) {
              for (const month of person.months ?? []) {
                const key = `${year}-${String(month.month).padStart(2, "0")}`;
                next[key] = (next[key] ?? 0) + (Number(month.target) || 0);
              }
              for (const quarter of person.quarters ?? []) {
                const key = `${year}-Q${quarter.quarter}`;
                next[key] = (next[key] ?? 0) + (Number(quarter.target) || 0);
              }
            }
            if (!(forecast.people && forecast.people.length > 0) && yearly > 0) {
              for (let month = 1; month <= 12; month += 1) {
                next[`${year}-${String(month).padStart(2, "0")}`] = yearly / 12;
              }
              for (let quarter = 1; quarter <= 4; quarter += 1) {
                next[`${year}-Q${quarter}`] = yearly / 4;
              }
            }
          } catch {
            /* Forecast is optional on this view — chart still shows projected $. */
          }
        }),
      );
      if (!cancelled) setPeriodTargets(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [view, canForecast, orgId, projectedBuckets]);

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.company_id.trim() || !formData.source_lead_id) return;
    const amount = parseAmount(formData.value, "Value", { allowZero: false });
    if (amount.error || amount.value == null) {
      setError(amount.error ?? "Value must be greater than zero.");
      return;
    }
    setError(null);
    try {
      await crmApi.createOpportunity(
        {
          title: formData.title,
          company_id: formData.company_id,
          source_lead_id: formData.source_lead_id,
          value: amount.value,
          stage: formData.stage || "lead_in",
          product_ids: formData.product_lines.map((line) => line.product_id),
          product_lines: formData.product_lines,
        },
        orgId,
      );
      setShowForm(false);
      setFormData({ title: "", company_name: "", company_id: "", source_lead_id: "", value: "", stage: "lead_in", product_lines: [] });
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create opportunity");
    }
  };

  const moveOpportunity = async (
    opp: Opportunity,
    nextStage: string,
    notify?: boolean,
    lossPayload?: { loss_reason: string; loss_reason_codes: string[] },
  ) => {
    const current = opp.stage ?? "lead_in";
    if (nextStage === current) return;
    if (nextStage === "closed_lost" && !lossPayload) {
      setPendingCloseLost(opp);
      setCloseLostCodes([]);
      setCloseLostComment("");
      return;
    }
    const previousBoard = board;
    const previousRows = opportunities;
    if (board) {
      const nextBuckets: Record<string, Opportunity[]> = {};
      for (const [stage, cards] of Object.entries(board.buckets)) {
        nextBuckets[stage] = cards.filter((card) => card.id !== opp.id);
      }
      const moved = { ...opp, stage: nextStage };
      nextBuckets[nextStage] = [...(nextBuckets[nextStage] ?? []), moved];
      setBoard({ ...board, buckets: nextBuckets });
    }
    setOpportunities(opportunities.map((row) => (row.id === opp.id ? { ...row, stage: nextStage } : row)));
    try {
      await crmApi.changeOpportunityStage(opp.id, { stage: nextStage, ...lossPayload }, orgId);
      if (notify) show("Saved!", "success");
      if (view === "table") await load();
    } catch (e) {
      if (previousBoard) setBoard(previousBoard);
      setOpportunities(previousRows);
      setError(e instanceof Error ? e.message : "Failed to move opportunity");
    }
  };

  const confirmCloseLost = async () => {
    if (!pendingCloseLost || closeLostCodes.length === 0 || !closeLostComment.trim()) return;
    setClosingLost(true);
    try {
      await moveOpportunity(pendingCloseLost, "closed_lost", true, {
        loss_reason: closeLostComment.trim(),
        loss_reason_codes: closeLostCodes,
      });
      setPendingCloseLost(null);
    } finally {
      setClosingLost(false);
    }
  };

  const toggleCloseLostCode = (code: string) => {
    setCloseLostCodes((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );
  };

  const handleDrop = (stage: string) => async (event: DragEvent) => {
    event.preventDefault();
    setOverStage(null);
    const oppId = event.dataTransfer.getData("text/plain");
    const source = Object.values(board?.buckets ?? {}).flat().find((card) => card.id === oppId);
    if (!source) return;
    await moveOpportunity(source, stage);
  };

  const generateQuote = async (opp: Opportunity) => {
    setQuotingId(opp.id);
    setError(null);
    try {
      const created = await crmApi.generateQuoteFromOpportunity(opp.id, orgId);
      show(`Quote ${created.quote_number ?? ""} created`.trim(), "success");
      navigate(crmHref(`/quotes/${created.id}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate quote");
    } finally {
      setQuotingId(null);
    }
  };

  const helpText =
    view === "projected"
      ? null
      : "Lead In is the first stage of the sales process. Open a card to work the deal through Qualifying, Proposal, Negotiation, and Close. Convert a lead from the Leads tab — that creates the opportunity automatically — or create one here from a lead. Drag a card into the pipeline stage that fits.";

  const renderProjectedCard = (opp: Opportunity) => (
    <KanbanCard
      key={opp.id}
      title={opportunityCardTitle(opp)}
      subtitle={[opp.opportunity_code, stageLabel(opp.stage)].filter(Boolean).join(" · ") || undefined}
      value={money(opp.value)}
      href={crmHref(`/opportunities/${opp.id}`)}
      onClick={() => navigate(crmHref(`/opportunities/${opp.id}`))}
      tags={
        <span className="crm-kanban-card-meta">
          {opp.expected_close_date && <span className="crm-badge">{opp.expected_close_date.slice(0, 10)}</span>}
          {opp.probability_pct != null && <span className="crm-badge">{opp.probability_pct}%</span>}
          {canQuote && (opp.stage ?? "") === "negotiation" && (
            <button
              type="button"
              className="crm-kanban-quote-btn"
              disabled={quotingId === opp.id}
              onClick={(event) => {
                event.stopPropagation();
                void generateQuote(opp);
              }}
            >
              {quotingId === opp.id ? "Generating…" : "Generate quote"}
            </button>
          )}
        </span>
      }
    />
  );

  const renderOpportunityCard = (opp: Opportunity, stageValue: string) => {
    const won = isWonOpportunity(opp);
    const terminal = isTerminalOpportunity(opp);
    const href = opportunityHref(opp, crmHref);
    return (
      <KanbanCard
        key={opp.id}
        title={opportunityCardTitle(opp)}
        subtitle={opp.opportunity_code || undefined}
        value={money(opp.value)}
        href={href}
        muted={terminal}
        backgroundColor={opportunityCardBackground(opp, boardDisplay)}
        draggable={canMove && !terminal}
        onClick={() => navigate(href)}
        onDragStart={(event) => {
          event.dataTransfer.setData("text/plain", opp.id);
          event.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={() => setOverStage(null)}
        tags={
          <span className="crm-kanban-card-meta crm-kanban-card-meta--board">
            <span className="crm-kanban-card-meta__main">
              {won ? (
                <span className="crm-badge crm-badge--won crm-badge--board-action">{wonOpportunityStatusLabel(opp)}</span>
              ) : terminal ? (
                <span className="crm-badge crm-badge--board-action">Closed Lost</span>
              ) : (
                <>
                  {opp.probability_pct != null && (
                    <span className="crm-badge">{opp.probability_pct}%</span>
                  )}
                  {canMove && (
                    <select
                      className="crm-select crm-kanban-status"
                      aria-label={`Move ${opportunityCardTitle(opp)}`}
                      value={opp.stage ?? "lead_in"}
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        void moveOpportunity(opp, event.target.value, true);
                      }}
                    >
                      {stages.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              )}
            </span>
            {!terminal && canAge && ageLabel(opp.age_days) && (
              <span className="crm-badge crm-badge--aging">{ageLabel(opp.age_days)}</span>
            )}
            {!terminal && !won && canQuote && stageValue === "negotiation" ? (
              <button
                type="button"
                className="crm-kanban-quote-btn"
                disabled={quotingId === opp.id}
                onClick={(event) => {
                  event.stopPropagation();
                  void generateQuote(opp);
                }}
              >
                {quotingId === opp.id ? "Generating…" : "Generate quote"}
              </button>
            ) : null}
          </span>
        }
      />
    );
  };

  const opportunityColumns = [
    { key: "id", label: "ID", hint: FIELD_HINTS.opportunity_id },
    { key: "title", label: "Title", primary: true, hint: FIELD_HINTS.opportunity_title },
    { key: "company", label: "Company", primary: true, hint: FIELD_HINTS.opportunity_company },
    { key: "value", label: "Value", primary: true, hint: FIELD_HINTS.opportunity_value },
    { key: "stage", label: "Stage", primary: true, hint: FIELD_HINTS.opportunity_stage },
    { key: "close", label: "Expected close", hint: FIELD_HINTS.opportunity_expected_close },
    { key: "probability", label: "Probability", hint: "Estimated chance of winning this deal (0–100%)." },
    ...(canAge ? [{ key: "aging", label: "Aging", hint: "How long this deal has been in its current stage." }] : []),
  ];

  const buildOpportunityRow = (o: Opportunity) => {
    const won = isWonOpportunity(o);
    const terminal = isTerminalOpportunity(o);
    const href = opportunityHref(o, crmHref);
    return {
      id: o.id,
      href,
      className: terminal ? "is-pipeline-muted" : undefined,
      cells: {
        id: o.opportunity_code ?? "—",
        title: <Link to={href}>{opportunityCardTitle(o)}</Link>,
        company: o.company_id ? (
          <Link to={crmHref(`/companies/${o.company_id}`)}>{o.company_name ?? "Company"}</Link>
        ) : (
          (o.company_name ?? "—")
        ),
        value: money(o.value) ?? "—",
        stage: won ? (
          <StatusBadge
            status="closed_won"
            options={OPPORTUNITY_STAGE_OPTIONS}
            className="crm-badge crm-badge--won"
          >
            {wonOpportunityStatusLabel(o)}
          </StatusBadge>
        ) : terminal ? (
          <StatusBadge status="closed_lost" options={OPPORTUNITY_STAGE_OPTIONS}>
            Closed Lost
          </StatusBadge>
        ) : canMove ? (
          <select
            value={o.stage ?? "lead_in"}
            onChange={(e) => void moveOpportunity(o, e.target.value, true)}
            className="crm-select"
            aria-label={`Move ${opportunityCardTitle(o)}`}
            title={FIELD_HINTS.opportunity_stage}
            onClick={(e) => e.stopPropagation()}
          >
            {DEFAULT_STAGES.map((stage) => (
              <option key={stage.value} value={stage.value} title={stage.hint}>
                {stage.label}
              </option>
            ))}
          </select>
        ) : (
          <StatusBadge status={o.stage} options={OPPORTUNITY_STAGE_OPTIONS}>
            {stageLabel(o.stage)}
          </StatusBadge>
        ),
        close: o.expected_close_date ?? "—",
        probability: o.probability_pct != null ? `${o.probability_pct}%` : "—",
        ...(canAge ? { aging: ageLabel(o.age_days) ?? "—" } : {}),
      },
      actions: (
        <Cluster gap={2}>
          <AskLoxleyButton record={{ kind: "opportunity", id: o.id, label: opportunityCardTitle(o) }} />
          <DeleteRecordButton
            capability="crm.opportunities"
            recordLabel={opportunityCardTitle(o)}
            size="sm"
            variant="ghost"
            onDeleted={async () => {
              await crmApi.deleteOpportunity(o.id, orgId);
              await load();
            }}
          />
        </Cluster>
      ),
    };
  };

  return (
    <ListWorkspace
      title="Opportunities"
      actions={
        <ResponsiveActions
          primary={
            <>
              <div className="crm-view-toggle" role="group" aria-label="Opportunity view">
                <button type="button" className={view === "board" ? "is-active" : ""} onClick={() => setView("board")}>
                  Board
                </button>
                <button type="button" className={view === "projected" ? "is-active" : ""} onClick={() => setView("projected")}>
                  Projected
                </button>
                <button type="button" className={view === "table" ? "is-active" : ""} onClick={() => setView("table")}>
                  Table
                </button>
              </div>
              <Button onClick={() => setShowForm(!showForm)}>{showForm ? "Cancel" : "New opportunity"}</Button>
            </>
          }
          more={
            canConfigure ? (
              <Button type="button" variant="secondary" onClick={() => setSettingsOpen(true)}>
                Opportunity settings
              </Button>
            ) : null
          }
        />
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search opportunities…"
            label="Search opportunities"
            hint="Search by deal name, company, contact, salesperson, or deal source."
          />
          {openOnly ? (
            <Cluster gap={2}>
              <p className="crm-help">Showing open deals.</p>
              <Button type="button" variant="ghost" size="sm" onClick={clearOpen}>
                Show won and lost too
              </Button>
            </Cluster>
          ) : null}
          {view === "projected" && (
            <div className="crm-view-toggle" role="group" aria-label="Projected period">
              <button type="button" className={grain === "month" ? "is-active" : ""} onClick={() => setGrain("month")}>
                By month
              </button>
              <button type="button" className={grain === "quarter" ? "is-active" : ""} onClick={() => setGrain("quarter")}>
                By quarter
              </button>
            </div>
          )}
          {view === "table" && (
            <StatusSlicer
              value={stageFilter}
              onChange={setStageFilter}
              options={DEFAULT_STAGES.map((stage) => stage.value)}
              label="Filter by stage"
              labelFor={(value) => DEFAULT_STAGES.find((stage) => stage.value === value)?.label ?? value}
              optionHints={DEFAULT_STAGES.map((stage) => ({ label: stage.label, meaning: stage.hint }))}
            />
          )}
          {(view === "table" || view === "board" || view === "projected") && (
            <GroupByControl
              label="Group opportunities by"
              value={groupBy}
              options={GROUP_BY_OPTIONS}
              onChange={(next) => setGroupBy(next as GroupBy)}
            />
          )}
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}
      {helpText ? <p className="crm-help">{helpText}</p> : null}

      {showForm && (
        <Card>
          <Stack gap={3}>
            <Input
              label="Title"
              tooltip={FIELD_HINTS.opportunity_title}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <p className="crm-help">
              Every opportunity starts from a lead, so the sale can be traced from first contact to invoice.
              {openLeads.length === 0 ? (
                <>
                  {" "}There are no open leads right now. <Link to={crmHref("/leads")}>Capture a lead first</Link>, then come back.
                </>
              ) : null}
            </p>
            <FieldLabel htmlFor="opp-lead" hint={FIELD_HINTS.opportunity_lead}>
              Lead
            </FieldLabel>
            <select
              id="opp-lead"
              className="crm-select"
              value={formData.source_lead_id}
              onChange={(event) => {
                const lead = leads.find((item) => item.id === event.target.value);
                setFormData({
                  ...formData,
                  source_lead_id: event.target.value,
                  company_id: lead?.company_id ?? formData.company_id,
                  company_name: lead?.company_name ?? formData.company_name,
                  title: formData.title || (lead?.company_name ?? ""),
                  product_lines: lead?.products?.map((item) => ({ product_id: item.id, quantity: 1 })) ?? formData.product_lines,
                });
              }}
            >
              <option value="">Choose a lead</option>
              {openLeads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {[lead.lead_code, `${lead.first_name ?? ""} ${lead.last_name ?? ""}`.trim(), lead.company_name]
                    .filter(Boolean)
                    .join(" · ")}
                </option>
              ))}
            </select>
            <CompanyLookup
              orgId={orgId}
              value={formData.company_name}
              companyId={formData.company_id}
              allowCreate={false}
              onChange={({ name, companyId }) => setFormData({ ...formData, company_name: name, company_id: companyId })}
            />
            <Input
              label="Value"
              tooltip={FIELD_HINTS.opportunity_value}
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            />
            <FieldLabel htmlFor="opp-stage" hint={FIELD_HINTS.opportunity_stage}>
              Stage
            </FieldLabel>
            <select
              id="opp-stage"
              className="crm-select"
              value={formData.stage}
              onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
            >
              {DEFAULT_STAGES.map((stage) => (
                <option key={stage.value} value={stage.value} title={stage.hint}>
                  {stage.label}
                </option>
              ))}
            </select>
            <ProductPicker
              orgId={orgId}
              value={formData.product_lines}
              quantities
              onChange={(product_lines: PickedProductLine[]) => setFormData((current) => ({ ...current, product_lines }))}
              onTotalChange={(total) => {
                if (total != null) setFormData((current) => ({ ...current, value: String(total) }));
              }}
            />
            <div className="crm-form-actions">
              <Button
                onClick={() => void handleCreate()}
                disabled={!formData.title.trim() || !formData.company_id.trim() || !formData.source_lead_id}
                title={!formData.source_lead_id ? "Choose the lead this opportunity comes from first." : undefined}
              >
                Create opportunity
              </Button>
            </div>
          </Stack>
        </Card>
      )}

      {loading ? (
        <Card>Loading...</Card>
      ) : view === "board" ? (
        loadFailed && !board ? (
          <Card>
            <EmptyState
              title="The board could not be loaded"
              description="This is a loading problem, not an empty pipeline. Your opportunities are safe."
              action={<Button onClick={() => void load()}>Try again</Button>}
            />
          </Card>
        ) : !board || opportunities.length === 0 ? (
          <Card>
            <EmptyState title="No opportunities found" description="Create your first opportunity to get started." />
          </Card>
        ) : (
          <>
          <StageFunnel
            segments={board.stages.map((stage) => ({
              value: stage.value,
              label: stage.label,
              count: (board.buckets[stage.value] ?? []).length,
              negative: stage.value === "closed_lost",
            }))}
            onSegmentDragEnter={canMove ? (stage) => setOverStage(stage) : undefined}
            onSegmentDrop={
              canMove
                ? (stage, event) => {
                    void handleDrop(stage)(event);
                  }
                : undefined
            }
          />
          <div className="crm-kanban" data-testid="opportunities-board" role="list" aria-label="Opportunity board">
            {board.stages.map((stage) => {
              const cards = board.buckets[stage.value] ?? [];
              const groups = groupBy === "none" ? [] : groupCardsBy(cards, groupBy, opportunityGroupKeyFor);
              return (
                <BoardColumn
                  key={stage.value}
                  className={`crm-kanban-column${overStage === stage.value ? " is-over" : ""}`}
                  title={stage.label}
                  count={cards.length}
                  totalValue={columnTotal(cards)}
                  onDragEnter={canMove ? () => setOverStage(stage.value) : undefined}
                  onDrop={(event) => void handleDrop(stage.value)(event)}
                >
                  {cards.length === 0 ? (
                    <p className="crm-kanban-empty">
                      {stage.value === "lead_in"
                        ? "New deals land here after you convert a lead or create an opportunity."
                        : "Drag a card here from another stage, or change its stage from the card menu."}
                    </p>
                  ) : groupBy === "none" ? (
                    cards.map((opp) => renderOpportunityCard(opp, stage.value))
                  ) : (
                    groups.map((group) => (
                      <KanbanStack
                        key={group.key}
                        label={group.label}
                        count={group.items.length}
                        totalValue={group.total ? money(group.total) : undefined}
                      >
                        {group.items.map((opp) => renderOpportunityCard(opp, stage.value))}
                      </KanbanStack>
                    ))
                  )}
                </BoardColumn>
              );
            })}
          </div>
          </>
        )
      ) : view === "projected" ? (
        opportunities.length === 0 && projectedBuckets.every((bucket) => bucket.cards.length === 0) ? (
          <Card>
            <EmptyState
              title="No projected landings yet"
              description="Add expected close dates on open deals to see when revenue would land if they were won."
            />
          </Card>
        ) : (
          <>
            <Card className="projected-chart-card">
              <ProjectedLandingChart
                points={chartPoints}
                grainLabel={grain === "month" ? "month, next 6 months" : "quarter over the next 6 months"}
              />
            </Card>
            <StageFunnel
              segments={projectedBuckets.map((bucket) => ({
                value: bucket.key,
                label: bucket.label,
                count: bucket.cards.length,
                negative: bucket.key === "unscheduled",
              }))}
            />
            <div className="crm-kanban" data-testid="opportunities-projected" role="list" aria-label="Projected landing">
              {projectedBuckets.map((bucket) => (
                <BoardColumn
                  key={bucket.key}
                  className="crm-kanban-column"
                  title={bucket.label}
                  count={bucket.cards.length}
                  totalValue={columnTotal(bucket.cards)}
                >
                  {bucket.cards.length === 0 ? (
                    <p className="crm-kanban-empty">No deals expected to close in this period.</p>
                  ) : groupBy === "none" ? (
                    bucket.cards.map(renderProjectedCard)
                  ) : (
                    groupCardsBy(bucket.cards, groupBy, opportunityGroupKeyFor).map((group) => (
                      <KanbanStack
                        key={group.key}
                        label={group.label}
                        count={group.items.length}
                        totalValue={group.total ? money(group.total) : undefined}
                      >
                        {group.items.map(renderProjectedCard)}
                      </KanbanStack>
                    ))
                  )}
                </BoardColumn>
              ))}
            </div>
            <p className="crm-help">
              Closed Lost is excluded. Deals without an expected close date sit in No close date and are left off the
              chart. Set salesperson targets under Forecast & KPI to draw the target tick on each bar.
            </p>
          </>
        )
      ) : opportunities.length === 0 ? (
        <Card>
          <EmptyState title="No opportunities found" description="Create your first opportunity to get started." />
        </Card>
      ) : groupBy === "none" ? (
        <Card className="crm-table-card">
          <ResponsiveDataTable caption="Opportunities" columns={opportunityColumns} rows={opportunities.map(buildOpportunityRow)} />
        </Card>
      ) : (
        <GroupTree
          groups={groupedOpportunities.map((group) => ({
            key: group.key,
            label: group.label,
            count: group.items.length,
            totalValue: group.total ? money(group.total) : undefined,
            children: (
              <ResponsiveDataTable
                caption={`Opportunities — ${group.label}`}
                columns={opportunityColumns}
                rows={group.items.map(buildOpportunityRow)}
              />
            ),
          }))}
        />
      )}
      <Drawer
        isOpen={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
          void load();
          void crmApi.getLossReasons(orgId).then((res) => setLossReasons(res.reasons)).catch(() => undefined);
        }}
        title="Opportunity settings"
      >
        {can("crm.field_options", "configure") ? (
          <>
            <h2 className="crm-section-title">Opportunity stage options</h2>
            <TabFieldOptionsSettings orgId={orgId} entity="opportunity" />
          </>
        ) : null}
        <BoardDisplaySettingsPanel orgId={orgId} entity="opportunity" onSaved={setBoardDisplay} />
        <LossReasonSettingsPanel orgId={orgId} />
      </Drawer>
      <CloseLostModal
        open={Boolean(pendingCloseLost)}
        reasons={lossReasons}
        selectedCodes={closeLostCodes}
        onToggleCode={toggleCloseLostCode}
        comment={closeLostComment}
        onCommentChange={setCloseLostComment}
        onCancel={() => setPendingCloseLost(null)}
        onConfirm={() => void confirmCloseLost()}
        saving={closingLost}
      />
      {view === "table" && (
        <LoadMore shown={opportunities.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="opportunities" />
      )}
    </ListWorkspace>
  );
}
